// Online Resources (#424): the library's folder tree, what a lesson may link,
// the documents a client writes, and the words.
//
// The rules are `core/src/resources.rs`, whose header is the argument — what
// MMS does and what it left us to decide, why the tree is resolved on every
// read rather than trusted, and why a link to a file that has gone degrades
// rather than breaks. This file is the web's and the functions' adapter: it
// reads a Firestore document into the core's record (`folderIn`,
// `resourceIn`) and hands back what the core answers, and it is in
// `src/domain/` because the message sender reads a lesson's links with the
// same code the drawer shows them with.
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

const str = (v) => (v == null ? null : String(v));
const id = (v) => (v == null || v === '' ? null : String(v));

// A `resourceFolders` document as the core reads one.
export function folderIn(f) {
  return { id: String(f?.id ?? ''), name: String(f?.name ?? ''), parentId: id(f?.parentId) };
}

// A `files` row as the core reads one. `folderId` absent is Home, which is
// where a row written without one sits.
export function resourceIn(r) {
  return {
    id: String(r?.id ?? ''),
    name: String(r?.name ?? ''),
    contentType: String(r?.contentType ?? ''),
    size: Number(r?.size) || 0,
    path: String(r?.path ?? ''),
    uploadedOn: String(r?.uploadedOn ?? ''),
    removedOn: id(r?.removedOn),
    folderId: id(r?.folderId),
  };
}

const folders = (list) => (list || []).map(folderIn);
const resources = (list) => (list || []).map(resourceIn);
const ids = (list) => (Array.isArray(list) ? list.filter((x) => typeof x === 'string') : []);

/** Links per lesson, and the two name bounds in UTF-16 units. */
export function resourceLimits() {
  const l = coreSync().resourceLimits();
  return { maxLinked: l.maxLinked, folderNameMax: l.folderNameMax, resourceNameMax: l.resourceNameMax };
}

/** `folderId` when that folder exists, else null (Home). */
export const currentFolder = (list, folderId) => coreSync().resourceCurrentFolder(folders(list), id(folderId)) ?? null;

/** Home, then each folder down to `folderId`: `[{ id, name }]`, Home's id null. */
export const breadcrumb = (list, folderId) => coreSync().resourceBreadcrumb(folders(list), id(folderId))
  .map((c) => ({ id: c.id ?? null, name: c.name }));

/** The folders directly inside `folderId`, in order — the stored documents. */
export function childFolders(list, folderId) {
  const byId = new Map((list || []).map((f) => [String(f.id), f]));
  return coreSync().resourceChildFolders(folders(list), id(folderId)).map((f) => byId.get(f.id)).filter(Boolean);
}

/** The files directly inside `folderId`, in order — the stored rows. */
export function folderResources(folderList, resourceList, folderId) {
  const byId = new Map((resourceList || []).map((r) => [String(r.id), r]));
  return coreSync().resourceFolderFiles(folders(folderList), resources(resourceList), id(folderId))
    .map((r) => byId.get(r.id)).filter(Boolean);
}

/** Where something may move: `[{ id, name, depth }]`, Home first. */
export const folderOptions = (list, moving = null) => coreSync().resourceFolderOptions(folders(list), id(moving))
  .map((o) => ({ id: o.id ?? null, name: o.name, depth: o.depth }));

const refusal = (r) => (r ? { code: r.code, message: r.message } : null);

export const moveFolderRefusal = (list, folderId, target) => refusal(
  coreSync().resourceMoveFolderRefusal(folders(list), String(folderId ?? ''), id(target)),
);

export const moveFileRefusal = (list, target) => refusal(coreSync().resourceMoveFileRefusal(folders(list), id(target)));

export const deleteFolderRefusal = (folderList, resourceList, folderId) => refusal(
  coreSync().resourceDeleteFolderRefusal(folders(folderList), resources(resourceList), String(folderId ?? '')),
);

export const folderNameRefusal = (name) => refusal(coreSync().resourceFolderNameRefusal(String(name ?? '')));

export const resourceNameRefusal = (name) => refusal(coreSync().resourceNameRefusal(String(name ?? '')));

export const uploadName = (raw) => coreSync().resourceUploadName(str(raw));

export const isLinkable = (row) => coreSync().resourceIsLinkable(resourceIn(row));

/**
 * A lesson's `resourceIds` read against the library: `[{ id, name, state,
 * path, contentType, message }]`, `state` being `live`, `removed` or
 * `missing`. `datePattern` draws a removed one's date in the studio's short
 * date, as `removedOn` in `src/lib/fileLimits.js` does (#589); a caller with
 * none in hand yet passes nothing, which reads the stored date back
 * unchanged.
 */
export const linkedResources = (resourceIds, resourceList, datePattern) => coreSync()
  .linkedResources(ids(resourceIds), resources(resourceList), String(datePattern ?? ''))
  .map((l) => ({
    id: l.id,
    name: l.name,
    state: l.state,
    path: l.path ?? null,
    contentType: l.contentType ?? null,
    message: l.message ?? null,
  }));

// ── Pictures in a lesson note (#531) ──────────────────────────────────────
//
// `resources.rs` says why a note names a file by id (`resource:<id>`) and
// which types it draws. These two are what a surface asks before the
// renderer runs; `noteFiles` below is the reading every surface makes of
// them, so the web, the email and (through the core directly) the phones
// resolve the same files the same way.

/** Whether a note draws a file of this type as a picture. */
export const isNoteImage = (contentType) => coreSync().resourceIsNoteImage(String(contentType ?? ''));

/** The library ids a note names, in order, once each, at most one `fileLinks` call's worth. */
export const noteFileIds = (source) => coreSync().noteFileIds(String(source ?? ''));

/**
 * The files a note names, read against the library: `[{ id, name, path,
 * image }]`. `path` is the object to mint a link to, for a file still in the
 * library and still stored, and null otherwise — a deleted or swept file,
 * which the renderer draws as its name. `image` is whether a note draws it as
 * a picture rather than linking it.
 */
export const noteFiles = (source, resourceList) => linkedResources(noteFileIds(source), resourceList)
  .map((l) => ({
    id: l.id,
    name: l.name,
    path: l.state === 'live' ? l.path : null,
    image: l.state === 'live' && isNoteImage(l.contentType),
  }));

/**
 * The Markdown that puts library file `id` into a note as a picture, with
 * `name` as its alt text, escaped so it reads back as exactly `name` —
 * `![Fingering chart.png](resource:file_…)`. The core's (#562), so the web
 * and both phones write the same characters. Throws for an id a note cannot
 * name, which no row the picker offers has.
 */
export function libraryImageMarkdown(name, id) {
  const md = coreSync().noteImageMarkdown(String(name ?? ''), String(id ?? ''));
  if (md == null) throw new Error(`libraryImageMarkdown: "${id}" is not a library id`);
  return md;
}

/**
 * Insert Image: library file `row` written into `text` in place of the
 * selection `start`..`end`, on a line of its own, with where the cursor goes
 * after it — `{ value, cursor, refusal }`. Refused whole (`refusal` is the
 * core's `{ code, message }` and `value` is `text` unchanged) rather than
 * clamped when the note would pass its bound: half a reference is a broken
 * one. An offset the box did not report is the end of the note.
 */
export function insertNoteImage(text, start, end, row) {
  const at = (n) => (Number.isInteger(n) ? n : undefined);
  const r = coreSync().noteImageInsert(String(text ?? ''), at(start), at(end), String(row?.name ?? ''), String(row?.id ?? ''));
  return { value: r.value, cursor: r.cursor, refusal: refusal(r.refusal) };
}

/**
 * The map the renderer takes as `library`, from `noteFiles` with a `url`
 * each: `{ [id]: { name, url, image } }`. A file with no url — gone, or no
 * link could be had — is still in the map, under its name.
 */
export function noteLibrary(files) {
  // No prototype: an id is whatever a note typed after `resource:`, and
  // `__proto__` is spelled with id characters.
  const out = Object.create(null);
  for (const f of files || []) {
    if (!f?.id) continue;
    out[f.id] = { name: String(f.name ?? ''), url: f.url || null, image: Boolean(f.image && f.url) };
  }
  return out;
}

export const linkSelection = (current, selected) => coreSync().resourceLinkSelection(ids(current), ids(selected));

export const linkRefusal = (current, selected) => refusal(coreSync().resourceLinkRefusal(ids(current), ids(selected)));

export const unlink = (current, resourceId) => coreSync().resourceUnlink(ids(current), String(resourceId ?? ''));

// ── The documents ─────────────────────────────────────────────────────────

// `serverTimestamp` is the writer's sentinel for `createdAt`; this directory
// imports no Firebase, so the caller hands it in.
export const folderDocument = (name, parentId, serverTimestamp) => documentFromRows(
  coreSync().resourceFolderDocument(String(name ?? ''), id(parentId)),
  serverTimestamp,
);

export const folderRenameDocument = (name) => documentFromRows(coreSync().resourceFolderRenameDocument(String(name ?? '')));

export const folderMoveDocument = (parentId) => documentFromRows(coreSync().resourceFolderMoveDocument(id(parentId)));

export const resourceRowDocument = ({ name, contentType, size, path, uploadedOn, folderId = null }) => documentFromRows(
  coreSync().resourceRowDocument(str(name), String(contentType), Number(size) || 0, String(path), String(uploadedOn), id(folderId)),
);

export const resourceRenameDocument = (name) => documentFromRows(coreSync().resourceRenameDocument(String(name ?? '')));

export const resourceMoveDocument = (folderId) => documentFromRows(coreSync().resourceMoveDocument(id(folderId)));

/** A word of the library's, by code; the code itself for one nobody wrote. */
export const resourceMessage = (code) => coreSync().resourceMessage(String(code)) ?? String(code);

export const resourceMessageCodes = () => coreSync().resourceMessageCodes();
